import { useMemo, type CSSProperties, type ReactElement } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { PublicClientApplication } from "@azure/msal-browser";
import type { AppConfig } from "./auth/authConfig";
import { AuthProvider } from "./auth/AuthProvider";
import { useAuth } from "./auth/useAuth";
import { createApiClient, InteractionRequiredError } from "./auth/apiClient";
import { ApiClientContext } from "./apiClientContext";
import { SignInGate } from "./SignInGate";
import { AuthenticatedShell } from "./AuthenticatedShell";
import { PostLoginRedirect } from "./PostLoginRedirect";
import { AdminRoutes } from "@admin/AdminRoutes";

export interface ExperienceShellProps {
    readonly msalInstance: PublicClientApplication;
    readonly config: AppConfig;
}

const mainStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background:
        "radial-gradient(circle at 30% 0%, oklch(0.22 0.05 270 / 0.6), transparent 55%), oklch(0.12 0.005 260)",
    color: "oklch(0.95 0 0)",
};

export function ExperienceShell({ msalInstance, config }: ExperienceShellProps): ReactElement {
    const apiClient = useMemo(
        () => createApiClient({ instance: msalInstance, config }),
        [msalInstance, config],
    );

    // QueryClient created once per shell mount. Iteration-0 keeps retries
    // for InteractionRequiredError off — the only meaningful response is to
    // re-authenticate, so spamming MSAL with silent acquires would do
    // nothing useful. Future workflows can extend this policy.
    const queryClient = useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        retry: (failureCount, error) => {
                            if (error instanceof InteractionRequiredError) return false;
                            return failureCount < 1;
                        },
                        refetchOnWindowFocus: false,
                    },
                },
            }),
        [],
    );

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider instance={msalInstance} config={config}>
                <ApiClientContext.Provider value={apiClient}>
                    <main style={mainStyle}>
                        <AuthGate />
                    </main>
                </ApiClientContext.Provider>
            </AuthProvider>
        </QueryClientProvider>
    );
}

/**
 * Decides between the unauthenticated and authenticated views. Lives
 * inside the shell (not in compositions) because the decision itself is
 * auth-aware — a future composition layer must stay ignorant of MSAL
 * internals to remain portable across capabilities.
 */
function AuthGate(): ReactElement {
    const auth = useAuth();

    if (!auth.isAuthenticated || auth.account === null) {
        return <SignInGate />;
    }

    return (
        <>
            <PostLoginRedirect />
            <Routes>
                <Route path="/" element={<AuthenticatedShell />} />
                <Route path="/admin/*" element={<AdminRoutes />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
}
